import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { formatGBP, relDate, ordinal } from "../lib/format";
import { MEDIA_TONES } from "../lib/format";
import { toast } from "sonner";
import {
  ChevronLeft, Trophy, TimerReset, Star, FileCheck2, Sparkles, Camera, PlayCircle, BarChart3, Flag, ArrowRight, Share2, Lock, Check, X
} from "lucide-react";

export default function DriverProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { role } = useRole();
  const [driver, setDriver] = useState(null);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const refresh = () => api.getDriver(id).then(setDriver);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!driver) return <Card className="p-8 text-sm text-muted-foreground">Loading driver…</Card>;

  const sr = driver.sponsor_readiness;
  const fund = driver.funding_need || {};
  const fundPct = fund.total_required ? Math.min(100, Math.round((fund.secured / fund.total_required) * 100)) : 0;

  const generate = async () => {
    setAiLoading(true);
    try {
      const r = await api.driverAISummary(id);
      setAiDraft(r);
      toast.success("Sponsor summary drafted", { description: "Advisory only — review before sharing." });
    } catch (e) {
      toast.error("Could not draft summary");
    } finally {
      setAiLoading(false);
    }
  };

  const approve = async () => {
    try {
      const pack = await api.createSponsorPack({
        driver_id: id,
        reviewer_name: role.name,
        reviewer_role: role.label,
        ai_summary: aiDraft,
        custom_notes: notes,
      });
      toast.success("Sponsor pack approved", { description: `Receipt ${pack.id} created.` });
      setApproveOpen(false); setNotes("");
      nav(`/sponsor-packs/${pack.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="driver-profile-page">
      <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-talent">
        <ChevronLeft className="h-4 w-4" /> Back to talent
      </Link>

      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-white">
        <div className="checker-stripe absolute top-0 right-0 w-64 h-64 pointer-events-none" />
        <div className="relative grid lg:grid-cols-5 gap-6 p-6 sm:p-8">
          <div className="lg:col-span-3 flex items-start gap-5">
            <div className="relative shrink-0">
              <img src={driver.avatar_url} alt="" className="h-24 w-24 rounded-lg object-cover bg-muted" />
              <span className="absolute -bottom-2 -right-2 h-9 min-w-9 px-1.5 rounded-md bg-[#F25E1D] text-white text-[14px] font-semibold grid place-items-center font-mono-ui">
                #{driver.race_number}
              </span>
            </div>
            <div className="min-w-0">
              <div className="tracking-eyebrow text-muted-foreground">{driver.series}</div>
              <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold mt-1">{driver.name}</h1>
              <div className="text-sm text-muted-foreground mt-1">
                Age {driver.age} · {driver.age_band} · {driver.location} · {driver.team}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border bg-white capitalize" data-testid="driver-racing-category">
                  {driver.racing_category || "—"}
                </span>
                <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border bg-white">
                  Race no. {driver.race_number}
                </span>
              </div>
              <p className="text-[15px] mt-3 max-w-2xl leading-relaxed">{driver.headline}</p>
            </div>
          </div>

          <Card className="lg:col-span-2 p-5 border-border bg-[#FAFAFB] space-y-4">
            <div className="flex items-center justify-between">
              <div className="tracking-eyebrow text-muted-foreground">Sponsor readiness</div>
              <span className="font-mono-ui font-semibold" style={{
                color: sr.score >= 80 ? "var(--rag-green-text)" : sr.score >= 60 ? "var(--rag-amber-text)" : "var(--rag-red-text)"
              }}>{sr.score}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full" style={{
                width: `${sr.score}%`,
                backgroundColor: sr.score >= 80 ? "var(--rag-green-text)" : sr.score >= 60 ? "var(--rag-amber-text)" : "var(--rag-red-text)"
              }} />
            </div>
            <ul className="text-xs space-y-1.5">
              {sr.items.map((i) => (
                <li key={i.label} className="flex items-center gap-2" data-testid={`sr-item-${i.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                  {i.ok ? <Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> : <X className="h-3.5 w-3.5 text-[var(--rag-red-text)]" />}
                  <span className={i.ok ? "" : "text-muted-foreground"}>{i.label}</span>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-muted-foreground border-t border-border pt-3">
              Operational readiness for next session: <StatusBadge status={driver.upcoming?.readiness?.overall || "neutral"} />{" "}
              {driver.upcoming?.title && <span className="font-mono-ui">· {driver.upcoming.title}</span>}
            </div>
          </Card>
        </div>
      </section>

      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList data-testid="driver-tabs">
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
          <TabsTrigger value="funding" data-testid="tab-funding">Funding</TabsTrigger>
          <TabsTrigger value="sponsor" data-testid="tab-sponsor">Sponsor pack</TabsTrigger>
          <TabsTrigger value="readiness" data-testid="tab-readiness">Operational readiness</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          <PerformanceBlock driver={driver} />
        </TabsContent>

        <TabsContent value="media" className="space-y-4">
          <MediaBlock driver={driver} />
        </TabsContent>

        <TabsContent value="funding" className="space-y-4">
          <FundingBlock fund={fund} fundPct={fundPct} />
        </TabsContent>

        <TabsContent value="sponsor" className="space-y-4">
          <SponsorPackBlock
            driver={driver}
            sr={sr}
            aiDraft={aiDraft}
            aiLoading={aiLoading}
            onGenerate={generate}
            onApprove={() => setApproveOpen(true)}
            role={role}
          />
          <Card className="p-5 border-border race-card flex items-center justify-between gap-3 bg-[#FAFAFB]">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Sponsor &amp; supporter directory</div>
              <div className="text-sm mt-1">Find supporters whose aims, credentials and values align with this driver.</div>
            </div>
            <Link to={`/sponsors?driver_id=${driver.id}`} data-testid="find-sponsor-matches">
              <Button variant="outline" size="sm">Find sponsor matches <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
            </Link>
          </Card>
          <Card className="p-5 border-border race-card flex items-center justify-between gap-3 bg-white">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Secondary · Bounded service layer</div>
              <div className="text-sm mt-1">
                Eligible support and kit — wallet-backed offers gated by role, age band, parent and officer approval.
              </div>
            </div>
            <Link to={`/drivers/${driver.id}/offers`} data-testid="open-eligible-offers">
              <Button variant="outline" size="sm">Eligible support &amp; kit <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
            </Link>
          </Card>
        </TabsContent>

        <TabsContent value="readiness" className="space-y-4">
          <ReadinessBlock driver={driver} />
        </TabsContent>
      </Tabs>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-pack-dialog">
          <DialogHeader>
            <DialogTitle>Approve sponsor pack</DialogTitle>
            <DialogDescription>
              You are about to approve this profile for external sharing as <b>{role.name}</b> ({role.label}). A receipt with a unique ID and shareable link will be created.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Optional context for the receipt (mitigations, scope of sharing, expiry)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="pack-approve-notes"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={approve} data-testid="pack-approve-confirm">Approve &amp; create pack</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- blocks ---------- */

function PerformanceBlock({ driver }) {
  const results = driver.recent_results || [];
  const lap = driver.lap_time_progression || [];
  const best = useMemo(() => results.reduce((m, r) => Math.min(m, r.position), 99), [results]);
  const delta = lap.length >= 2 ? (lap[0].time_seconds - lap[lap.length - 1].time_seconds) : 0;

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2 p-6 border-border race-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Recent results</div>
            <h3 className="text-lg font-semibold mt-0.5">Last {results.length} rounds</h3>
          </div>
          <div className="flex items-center gap-2 text-xs font-mono-ui text-muted-foreground">
            <Trophy className="h-3.5 w-3.5" /> Best: {best <= 90 ? ordinal(best) : "—"}
          </div>
        </div>
        <div className="divide-y divide-border">
          {results.map((r) => (
            <div key={r.event + r.date} className="flex items-center py-2.5 gap-4 text-sm" data-testid={`result-row-${r.event}`}>
              <div className="w-10 h-10 grid place-items-center rounded-md border border-border font-mono-ui font-semibold text-[15px]" style={{
                color: r.position === 1 ? "#9A6212" : r.position <= 3 ? "var(--rag-green-text)" : "inherit"
              }}>P{r.position}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{r.event}</div>
                <div className="text-[11px] text-muted-foreground font-mono-ui">Best lap {r.lap_best} · {relDate(r.date)}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-border race-card">
        <div className="flex items-center justify-between mb-3">
          <div className="tracking-eyebrow text-muted-foreground">Lap-time progression</div>
          <span className="text-[11px] font-mono-ui" style={{ color: delta > 0 ? "var(--rag-green-text)" : "var(--rag-red-text)" }}>
            {delta > 0 ? `▼ ${delta.toFixed(2)}s` : `${delta.toFixed(2)}s`}
          </span>
        </div>
        <Sparkline data={lap.map((p) => p.time_seconds)} />
        <div className="text-[11px] text-muted-foreground font-mono-ui mt-2">
          {lap.length >= 1 && (
            <>From {lap[0].time_seconds.toFixed(2)}s ({relDate(lap[0].date)}) → {lap[lap.length - 1].time_seconds.toFixed(2)}s ({relDate(lap[lap.length - 1].date)})</>
          )}
        </div>

        <div className="mt-5">
          <div className="tracking-eyebrow text-muted-foreground mb-2">Training milestones</div>
          <ul className="space-y-2 text-sm">
            {(driver.training_milestones || []).map((m) => (
              <li key={m.label} className="flex items-start gap-2"><Star className="h-3.5 w-3.5 mt-0.5 text-[#F25E1D]" /><span>{m.label}<span className="text-[11px] text-muted-foreground font-mono-ui ml-1">· {relDate(m.date)}</span></span></li>
            ))}
          </ul>
        </div>
      </Card>

      <Card className="lg:col-span-3 p-6 border-border race-card">
        <div className="tracking-eyebrow text-muted-foreground">Endorsements</div>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          {(driver.endorsements || []).map((e) => (
            <div key={e.by + e.date} className="rounded-lg border border-border p-4" data-testid={`endorsement-${e.by}`}>
              <p className="text-[15px] leading-relaxed">“{e.quote}”</p>
              <div className="text-[11px] text-muted-foreground mt-2 font-mono-ui">— {e.by}, {e.role} · {relDate(e.date)}</div>
            </div>
          ))}
          {(!driver.endorsements || driver.endorsements.length === 0) && (
            <div className="text-sm text-muted-foreground">No endorsements on file yet. Ask a coach to add one to unlock sponsor readiness.</div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Sparkline({ data }) {
  if (!data || data.length < 2) return <div className="text-xs text-muted-foreground">Not enough data points.</div>;
  const w = 280, h = 70, p = 6;
  const min = Math.min(...data), max = Math.max(...data);
  const range = (max - min) || 1;
  const pts = data.map((v, i) => {
    const x = p + (i * (w - 2 * p)) / (data.length - 1);
    const y = h - p - ((max - v) / range) * (h - 2 * p);  // lower time = higher line
    return [x, y];
  });
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[70px]">
      <path d={path} fill="none" stroke="#F25E1D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.5" fill={i === pts.length - 1 ? "#F25E1D" : "#1A1A1F"} />
      ))}
    </svg>
  );
}

function MediaBlock({ driver }) {
  const media = driver.media || [];
  return (
    <Card className="p-6 border-border race-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Media assets</div>
          <h3 className="text-lg font-semibold mt-0.5">Sponsor-ready samples</h3>
        </div>
        <span className="text-[11px] text-muted-foreground">{media.length} on file</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {media.map((m, i) => (
          <div key={i} className={`relative rounded-lg overflow-hidden bg-gradient-to-br ${MEDIA_TONES[m.tone] || MEDIA_TONES.paddock} aspect-[4/3]`} data-testid={`media-${i}`}>
            <div className="absolute inset-0 grid place-items-center">
              {m.kind === "reel" ? <PlayCircle className="h-10 w-10 opacity-80" /> : m.kind === "stat" ? <BarChart3 className="h-10 w-10 opacity-80" /> : <Camera className="h-10 w-10 opacity-80" />}
            </div>
            <div className="absolute top-2 left-2 text-[10px] tracking-eyebrow font-mono-ui bg-black/30 backdrop-blur px-1.5 py-0.5 rounded">{m.badge}</div>
            <div className="absolute bottom-2 left-2 right-2 text-[12px] font-medium drop-shadow">{m.title}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function FundingBlock({ fund, fundPct }) {
  return (
    <Card className="p-6 border-border race-card">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">
          <div className="tracking-eyebrow text-muted-foreground">Funding need</div>
          <div>
            <div className="text-3xl font-semibold">{formatGBP(fund.gap)}</div>
            <div className="text-xs text-muted-foreground font-mono-ui">remaining to secure</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">{formatGBP(fund.secured)} of {formatGBP(fund.total_required)} · {fundPct}%</div>
            <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full bg-[#F25E1D]" style={{ width: `${fundPct}%` }} />
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="tracking-eyebrow text-muted-foreground mb-2">Breakdown</div>
          <ul className="divide-y divide-border">
            {(fund.categories || []).map((c) => (
              <li key={c.label} className="flex items-center justify-between py-2.5 text-sm" data-testid={`fund-cat-${c.label}`}>
                <span>{c.label}</span>
                <span className="font-mono-ui font-medium">{formatGBP(c.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

function SponsorPackBlock({ driver, sr, aiDraft, aiLoading, onGenerate, onApprove, role }) {
  const canApprove = ["club_secretary", "committee"].includes(role.id);
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2 p-6 border-border race-card space-y-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 tracking-eyebrow text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> AI sponsor draft
          </div>
          <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border signal-bg signal-text">Advisory only</span>
        </div>

        {!aiDraft && (
          <div className="text-sm text-muted-foreground leading-relaxed space-y-4">
            <p>
              Generate a structured sponsor narrative from this driver's facts: results, lap times, milestones, endorsements and funding need.
              The draft is advisory — a club officer reviews before anything is shared externally.
            </p>

            {/* Bounded AI preflight — what the AI is and isn't allowed to access */}
            <div
              className="rounded-md border border-[#F25E1D]/30 bg-[#FFF7F2] p-3 grid sm:grid-cols-2 gap-3 text-[12px]"
              data-testid="bounded-ai-preflight"
            >
              <div>
                <div className="tracking-eyebrow text-[#1F4A2D] inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> AI may access
                </div>
                <ul className="mt-1.5 space-y-1 text-foreground/80">
                  <li>· Approved profile summary</li>
                  <li>· Sponsor-readiness summary</li>
                  <li>· Selected performance summary</li>
                  <li>· Selected media references (refs, not raw files)</li>
                  <li>· Sponsor aims and eligibility criteria</li>
                </ul>
              </div>
              <div>
                <div className="tracking-eyebrow text-[#7A2E16] inline-flex items-center gap-1">
                  <Lock className="h-3 w-3" /> AI must not access
                </div>
                <ul className="mt-1.5 space-y-1 text-foreground/80">
                  <li>· Full date of birth</li>
                  <li>· Family details</li>
                  <li>· Safeguarding notes</li>
                  <li>· Private contact details</li>
                  <li>· Withheld media / assets · Raw wallet contents · KATLAS API key</li>
                </ul>
              </div>
              <div className="sm:col-span-2 text-[11px] italic text-muted-foreground border-t border-[#F25E1D]/20 pt-2 mt-1">
                AI drafts from approved evidence only. It cannot approve an introduction or access withheld profile data.
              </div>
            </div>

            <div className="mt-4">
              <Button onClick={onGenerate} disabled={aiLoading} data-testid="generate-ai-draft">
                <Sparkles className="h-4 w-4 mr-1.5" /> {aiLoading ? "Drafting…" : "Draft with AI · bounded prompt"}
              </Button>
            </div>
          </div>
        )}

        {aiDraft && (
          <div className="space-y-4" data-testid="ai-draft-panel">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Headline</div>
              <p className="text-lg font-semibold mt-1">{aiDraft.headline}</p>
            </div>
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Summary</div>
              <p className="text-[15px] mt-1 leading-relaxed">{aiDraft.summary_paragraph}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="tracking-eyebrow text-muted-foreground">Strengths</div>
                <ul className="mt-1.5 space-y-1 text-sm">
                  {aiDraft.strengths?.map((s, i) => <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-[var(--rag-green-text)]" /><span>{s}</span></li>)}
                </ul>
              </div>
              <div>
                <div className="tracking-eyebrow text-muted-foreground">Asks</div>
                <ul className="mt-1.5 space-y-1 text-sm">
                  {aiDraft.asks?.map((s, i) => <li key={i} className="flex gap-2"><Flag className="h-3.5 w-3.5 mt-0.5 text-[#F25E1D]" /><span>{s}</span></li>)}
                </ul>
              </div>
            </div>
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Plausible partner fit</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {aiDraft.partner_fit?.map((p) => <span key={p} className="text-xs font-mono-ui px-2 py-0.5 rounded-full border border-border">{p}</span>)}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground italic border-t border-border pt-3">
              Confidence: <b className="not-italic">{aiDraft.confidence}</b>. {aiDraft.caveats?.join(" ")}
              <div className="font-mono-ui text-[10px] mt-1">{aiDraft._source}</div>
            </div>
            <div className="pt-2 flex items-center gap-2">
              <Button onClick={onGenerate} variant="ghost" size="sm" data-testid="redraft-ai">Redraft</Button>
              <div className="flex-1" />
              <Button onClick={onApprove} disabled={!sr.ready_to_share || !canApprove} data-testid="approve-pack-btn">
                <FileCheck2 className="h-4 w-4 mr-1.5" /> Approve &amp; create sponsor pack
              </Button>
            </div>
            {!sr.ready_to_share && <div className="text-xs text-muted-foreground">Cannot approve while {sr.items.filter((i) => !i.ok).length} readiness item(s) are open.</div>}
            {!canApprove && <div className="text-xs text-muted-foreground">Switch to <b>Club Secretary</b> or <b>Committee</b> to approve.</div>}
          </div>
        )}
      </Card>

      <Card className="p-6 border-border race-card space-y-3">
        <div className="tracking-eyebrow text-muted-foreground">Previous packs</div>
        {driver.packs?.length === 0 && <div className="text-sm text-muted-foreground">No packs approved yet.</div>}
        <ul className="space-y-2">
          {driver.packs?.map((p) => (
            <li key={p.id} className="text-xs font-mono-ui flex items-center justify-between gap-2" data-testid={`pack-row-${p.id}`}>
              <Link to={`/sponsor-packs/${p.id}`} className="hover:underline">{p.id}</Link>
              <span className="text-muted-foreground">{relDate(p.created_at)}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function ReadinessBlock({ driver }) {
  const up = driver.upcoming;
  if (!up) return <Card className="p-6 text-sm text-muted-foreground">No upcoming session linked.</Card>;
  const r = up.readiness;
  return (
    <Card className="p-6 border-border race-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Next session</div>
          <h3 className="text-lg font-semibold mt-0.5">{up.title}</h3>
          <div className="text-[11px] text-muted-foreground font-mono-ui">{new Date(up.start_at).toLocaleString()}</div>
        </div>
        <StatusBadge status={r.overall} size="lg" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4">
        {Object.entries(r.sub_scores).map(([k, v]) => (
          <div key={k} className="rounded-md border border-border p-2.5 text-xs">
            <div className="text-muted-foreground capitalize">{k.replace("_", " ")}</div>
            <div className="font-mono-ui font-medium mt-0.5" style={{
              color: v === "green" ? "var(--rag-green-text)" : v === "amber" ? "var(--rag-amber-text)" : "var(--rag-red-text)"
            }}>{v.toUpperCase()}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-muted-foreground mt-4 flex items-center gap-2">
        <Lock className="h-3.5 w-3.5" /> Until operational items clear, this driver's sponsor pack stays in draft.
      </div>
      <div className="mt-4">
        <Link to={`/events/${up.id}`}>
          <Button variant="outline" size="sm" data-testid="open-linked-event">Open session readiness <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
        </Link>
      </div>
    </Card>
  );
}
